import { createTheme } from '@mui/material/styles';

// Nowoczesna paleta kolorów dla domu mody
const theme = createTheme({
  palette: {
    primary: {
      main: '#4a69bb', // Kolor główny z dokumentu UI
      light: '#7b8fd1',
      dark: '#2a4188',
      contrastText: '#fff',
    },
    secondary: {
      main: '#6384e0', // Kolor uzupełniający z dokumentu UI
      light: '#8ca9ff',
      dark: '#4062ad',
      contrastText: '#fff',
    },
    error: {
      main: '#d32f2f',
    },
    background: {
      default: '#f8f9fa', // Jaśniejsze tło strony
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.75rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.85rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.05em',
    },
    subtitle1: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 500,
          letterSpacing: '0.05em',
          transition: 'all 0.3s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'box-shadow 0.3s ease',
            '&.Mui-focused': {
              boxShadow: '0px 0px 0px 2px rgba(74, 105, 187, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
            fontWeight: 500,
            letterSpacing: '0.05em',
          },
        },
        indicator: {
          height: 3,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(74, 105, 187, 0.1)',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.05)',
    '0px 6px 12px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.08)',
    '0px 10px 20px rgba(0, 0, 0, 0.09)',
    '0px 12px 24px rgba(0, 0, 0, 0.09)',
    '0px 14px 28px rgba(0, 0, 0, 0.10)',
    '0px 16px 32px rgba(0, 0, 0, 0.10)',
    '0px 18px 36px rgba(0, 0, 0, 0.11)',
    '0px 20px 40px rgba(0, 0, 0, 0.11)',
    '0px 22px 44px rgba(0, 0, 0, 0.12)',
    '0px 24px 48px rgba(0, 0, 0, 0.12)',
    '0px 26px 52px rgba(0, 0, 0, 0.13)',
    '0px 28px 56px rgba(0, 0, 0, 0.13)',
    '0px 30px 60px rgba(0, 0, 0, 0.14)',
    '0px 32px 64px rgba(0, 0, 0, 0.14)',
    '0px 34px 68px rgba(0, 0, 0, 0.15)',
    '0px 36px 72px rgba(0, 0, 0, 0.15)',
    '0px 38px 76px rgba(0, 0, 0, 0.16)',
    '0px 40px 80px rgba(0, 0, 0, 0.16)',
    '0px 42px 84px rgba(0, 0, 0, 0.17)',
    '0px 44px 88px rgba(0, 0, 0, 0.17)',
    '0px 46px 92px rgba(0, 0, 0, 0.18)',
    '0px 48px 96px rgba(0, 0, 0, 0.18)'
  ],
});

export default theme;
